import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInputWithCode } from "@/components/PhoneInputWithCode";
import { toast } from "sonner";
import axios from "axios";
import {
  Clock, Plus, Send, Check, X, Eye, Mail,
  MapPin, Loader2, ExternalLink, Building2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const OBSERVATION_TYPES = ["Occupational Therapy", "Physical Therapy", "Other"];

const EMPTY_FORM = {
  facility_name: "",
  city: "",
  state: "",
  supervisor_first_name: "",
  supervisor_last_name: "",
  supervisor_email: "",
  supervisor_phone: "",
  supervisor_phone_country_code: "+1|US",
  hours_requested: "",
  observation_type: "",
  dates_completed: "",
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY",
  "LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND",
  "OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"
];

export default function ObservationHoursSection({ observations, setObservations, appId, token }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [sendingId, setSendingId] = useState(null);

  const totalRequested = observations.reduce((sum, o) => sum + (parseInt(o.hours_requested) || 0), 0);
  const totalConfirmed = observations.reduce((sum, o) => sum + (parseInt(o.hours_confirmed) || 0), 0);
  const completedCount = observations.filter(o => o.request_stage === "Completed").length;
  const progressPct = totalRequested > 0 ? Math.min((totalConfirmed / totalRequested) * 100, 100) : 0;

  const validateForm = () => {
    const e = {};
    if (!form.facility_name.trim()) e.facility_name = true;
    if (!form.supervisor_first_name.trim()) e.supervisor_first_name = true;
    if (!form.supervisor_last_name.trim()) e.supervisor_last_name = true;
    if (!form.supervisor_email.trim()) e.supervisor_email = true;
    if (!form.hours_requested) e.hours_requested = true;
    if (!form.observation_type) e.observation_type = true;
    setErrors(e);
    if (Object.keys(e).length) toast.error("Please fill in all required fields");
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
    const newObs = {
      id: Date.now().toString(),
      ...form,
      hours_requested: parseInt(form.hours_requested) || 0,
      request_stage: "Pending",
      date_requested: now,
      date_confirmed: null,
      hours_confirmed: 0,
    };
    setObservations([...observations, newObs]);
    setForm({ ...EMPTY_FORM });
    setErrors({});
    setShowForm(false);
    toast.success("Observation hour request added. Send the email to notify your supervisor.");
  };

  const handleSendEmail = async (obs) => {
    if (!appId || !token) return;
    setSendingId(obs.id);
    try {
      await axios.put(
        `${API}/applications/${appId}`,
        { observation_hours: observations },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const response = await axios.post(
        `${API}/applications/${appId}/observations/${obs.id}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setObservations(prev =>
        prev.map(o => o.id === obs.id ? { ...o, request_stage: "Sent", token: data.token } : o)
      );
      const params = new URLSearchParams({
        supervisorName: `${obs.supervisor_first_name} ${obs.supervisor_last_name}`,
        applicantName: data.applicant_name,
        program: data.program,
        portalToken: data.token,
        supervisorEmail: obs.supervisor_email,
      });
      window.open(
        `${window.location.origin}/mock-observation-email?${params.toString()}`,
        "_blank"
      );
      toast.success("Observation verification request sent!");
    } catch (err) {
      console.error("Send observation error:", err);
      toast.error("Failed to send observation request");
    } finally {
      setSendingId(null);
    }
  };

  const handleRemove = (id) => {
    setObservations(observations.filter(o => o.id !== id));
  };

  const getStatusConfig = (stage) => {
    switch (stage) {
      case "Completed":
        return { color: "#739600", bg: "bg-[#739600]/15", border: "border-[#739600]/30", icon: Check, label: "Completed" };
      case "Sent":
        return { color: "#00677F", bg: "bg-[#00677F]/15", border: "border-[#00677F]/30", icon: Send, label: "Sent" };
      default:
        return { color: "#FF9800", bg: "bg-[#FF9800]/15", border: "border-[#FF9800]/30", icon: Clock, label: "Pending" };
    }
  };

  const inputCls = (name) => `h-11 bg-black/30 border-white/[0.08] text-white rounded-xl px-4 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600 ${errors[name] ? "border-red-400/50" : ""}`;
  const labelCls = "text-sm text-slate-300 flex items-center gap-1.5 mb-1.5";

  return (
    <div className="space-y-8" data-testid="observation-hours-section">

      {/* === Top Hero Card === */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#00677F]/20 via-transparent to-[#7B68EE]/10" />
        <div className="relative p-8">
          <div className="flex items-start justify-between gap-8">
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00677F]/20 border border-[#00677F]/30">
                <Eye className="w-3.5 h-3.5 text-[#A1D8E0]" />
                <span className="text-[#A1D8E0] text-xs font-medium tracking-wide uppercase">Observation Hours</span>
              </div>
              <p className="text-slate-400 text-xs font-medium">
                Observation hours are optional and not required for admission.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed max-w-2xl">
                The OT department at USAHS recommends that all students observe the field of Occupational Therapy for at least 20 hours prior to the start of the program. It provides understanding of the profession and helps better prepare students. Though hours are not required for admission, we strongly encourage them.
              </p>
            </div>
            <div className="flex flex-col items-center gap-2 shrink-0">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke={totalConfirmed >= 20 ? "#739600" : "#00677F"}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${progressPct * 2.51} 251`}
                    className="transition-all duration-700"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl font-bold ${totalConfirmed >= 20 ? "text-[#739600]" : "text-white"}`}>
                    {totalConfirmed}
                  </span>
                  <span className="text-slate-500 text-[10px] uppercase tracking-wider">hours</span>
                </div>
              </div>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider">Confirmed</span>
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/[0.05]">
            {[
              "Add Observation Entry",
              "Send Verification Email",
              "Supervisor Verifies Hours",
              "Status: Completed"
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2 flex-1">
                <div className="w-5 h-5 rounded-full bg-[#00677F]/30 border border-[#00677F]/40 flex items-center justify-center shrink-0">
                  <span className="text-[10px] text-[#A1D8E0] font-bold">{i + 1}</span>
                </div>
                <span className="text-slate-400 text-xs">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* === Add Button + Total === */}
      <div className="flex items-center justify-between">
        <Button
          onClick={() => setShowForm(true)}
          className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-6 h-11 shadow-[0_0_20px_rgba(0,103,127,0.3)] hover:shadow-[0_0_30px_rgba(0,103,127,0.4)] transition-all"
          data-testid="add-observation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Hour Request
        </Button>
        <div className="text-sm text-slate-400">
          Total Hours Confirmed <span className="text-white font-semibold">{totalConfirmed}</span> / <span className="text-slate-500">{totalRequested} requested</span>
        </div>
      </div>

      {/* === Observation Cards === */}
      {observations.length > 0 ? (
        <div className="space-y-3">
          {observations.map((obs) => {
            const status = getStatusConfig(obs.request_stage);
            const StatusIcon = status.icon;
            return (
              <div
                key={obs.id}
                className="group relative overflow-hidden rounded-2xl border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300"
                data-testid={`obs-row-${obs.id}`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center gap-5 p-5">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${status.bg} border ${status.border}`}>
                    <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium text-sm">
                        {obs.supervisor_first_name} {obs.supervisor_last_name}
                      </h4>
                      <span className="px-2 py-0.5 rounded-full bg-[#7B68EE]/10 border border-[#7B68EE]/20 text-[#7B68EE] text-[10px] font-medium uppercase tracking-wider">
                        {obs.observation_type}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-[#00677F]/10 border border-[#00677F]/20 text-[#A1D8E0] text-[10px] font-medium">
                        {obs.hours_requested}h requested
                      </span>
                      {obs.hours_confirmed > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-[#739600]/10 border border-[#739600]/20 text-[#739600] text-[10px] font-medium">
                          {obs.hours_confirmed}h confirmed
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${status.bg} border ${status.border}`}
                        style={{ color: status.color }}
                        data-testid={`obs-status-${obs.id}`}
                      >
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5">
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Building2 className="w-3 h-3" /> {obs.facility_name}
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                        <Mail className="w-3 h-3" /> {obs.supervisor_email}
                      </span>
                      {obs.city && (
                        <span className="flex items-center gap-1.5 text-slate-500 text-xs">
                          <MapPin className="w-3 h-3" /> {obs.city}, {obs.state}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {obs.request_stage === "Pending" && (
                      <Button
                        size="sm"
                        onClick={() => handleSendEmail(obs)}
                        disabled={sendingId === obs.id}
                        className="bg-[#00677F] hover:bg-[#135163] text-white rounded-full px-4 h-9 text-xs shadow-[0_0_12px_rgba(0,103,127,0.3)]"
                        data-testid={`obs-send-${obs.id}`}
                      >
                        {sendingId === obs.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Send Email
                      </Button>
                    )}
                    {obs.request_stage === "Sent" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSendEmail(obs)}
                        disabled={sendingId === obs.id}
                        className="border-[#00677F]/40 text-[#A1D8E0] hover:bg-[#00677F]/10 rounded-full px-4 h-9 text-xs"
                        data-testid={`obs-resend-${obs.id}`}
                      >
                        {sendingId === obs.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                        ) : (
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Resend
                      </Button>
                    )}
                    <div className="text-right space-y-1">
                      <div className="text-slate-500 text-xs">
                        Requested <span className="text-slate-400">{obs.date_requested}</span>
                      </div>
                      {obs.date_confirmed && (
                        <div className="text-slate-500 text-xs">
                          Confirmed <span className="text-[#739600]">{new Date(obs.date_confirmed).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {obs.request_stage === "Pending" && (
                    <button
                      onClick={() => handleRemove(obs.id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-red-400 p-1"
                      data-testid={`obs-remove-${obs.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-white/[0.08] p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-4">
            <Eye className="w-6 h-6 text-slate-600" />
          </div>
          <p className="text-slate-500 text-sm">No observation hours yet</p>
          <p className="text-slate-600 text-xs mt-1">Click "Add Hour Request" to get started</p>
        </div>
      )}

      {/* === Modal === */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="observation-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setShowForm(false); setErrors({}); }} />
          <div className="relative w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl shadow-black/40 animate-in fade-in zoom-in-95 duration-200">
            <div className="relative overflow-hidden px-6 py-5">
              <div className="absolute inset-0 bg-gradient-to-r from-[#00677F] to-[#00677F]/80" />
              <div className="relative flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h4 className="text-white text-lg font-semibold">Observation Hour Request</h4>
                  <p className="text-white/60 text-xs">Enter your observation details and supervisor information</p>
                </div>
              </div>
            </div>

            <div className="bg-[#0f1419] border-x border-white/[0.08] p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              <div>
                <Label className={labelCls}><span className="text-red-400">*</span> Place Name Where Observation Hours Were Completed</Label>
                <Input placeholder="Facility or clinic name" value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} className={inputCls("facility_name")} data-testid="obs-facility" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}>City</Label>
                  <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls("city")} data-testid="obs-city" />
                </div>
                <div>
                  <Label className={labelCls}>State</Label>
                  <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                    <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl focus:border-[#A1D8E0]`} data-testid="obs-state">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1419] border-white/10 max-h-[200px]">
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s} className="text-slate-300 hover:bg-white/5">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Supervisor First Name</Label>
                  <Input placeholder="First Name" value={form.supervisor_first_name} onChange={(e) => setForm({ ...form, supervisor_first_name: e.target.value })} className={inputCls("supervisor_first_name")} data-testid="obs-sup-first" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Supervisor Last Name</Label>
                  <Input placeholder="Last Name" value={form.supervisor_last_name} onChange={(e) => setForm({ ...form, supervisor_last_name: e.target.value })} className={inputCls("supervisor_last_name")} data-testid="obs-sup-last" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Supervisor Work Email</Label>
                  <Input type="email" placeholder="email@work.com" value={form.supervisor_email} onChange={(e) => setForm({ ...form, supervisor_email: e.target.value })} className={inputCls("supervisor_email")} data-testid="obs-sup-email" />
                </div>
                <div>
                  <Label className={labelCls}>Supervisor Work Phone</Label>
                  <PhoneInputWithCode
                    value={form.supervisor_phone}
                    countryCode={form.supervisor_phone_country_code || "+1|US"}
                    onPhoneChange={(val) => setForm({ ...form, supervisor_phone: val })}
                    onCodeChange={(val) => setForm({ ...form, supervisor_phone_country_code: val })}
                    testId="obs-sup-phone"
                    theme="dark"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Hours Completed at This Location</Label>
                  <Input type="number" placeholder="0" value={form.hours_requested} onChange={(e) => setForm({ ...form, hours_requested: e.target.value })} className={inputCls("hours_requested")} data-testid="obs-hours" />
                </div>
                <div>
                  <Label className={labelCls}><span className="text-red-400">*</span> Subject Matter Observed</Label>
                  <Select value={form.observation_type} onValueChange={(v) => setForm({ ...form, observation_type: v })}>
                    <SelectTrigger className={`h-11 bg-black/30 border-white/[0.08] text-white rounded-xl focus:border-[#A1D8E0] ${errors.observation_type ? "border-red-400/50" : ""}`} data-testid="obs-type">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0f1419] border-white/10">
                      {OBSERVATION_TYPES.map(t => (
                        <SelectItem key={t} value={t} className="text-slate-300 hover:bg-white/5">{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className={labelCls}>Date/Timeframe Completed</Label>
                <Textarea
                  placeholder="e.g., June 2024 - August 2024, or specific dates"
                  value={form.dates_completed}
                  onChange={(e) => setForm({ ...form, dates_completed: e.target.value })}
                  className="bg-black/30 border-white/[0.08] text-white rounded-xl px-4 py-3 focus:border-[#A1D8E0] focus:ring-2 focus:ring-[#A1D8E0]/20 placeholder:text-slate-600 min-h-[80px]"
                  data-testid="obs-dates"
                />
              </div>
            </div>

            <div className="bg-[#0f1419] border border-white/[0.08] border-t-0 rounded-b-2xl px-6 pb-6 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowForm(false); setErrors({}); setForm({ ...EMPTY_FORM }); }}
                className="border-white/20 text-white hover:bg-white/5 rounded-xl px-6"
                data-testid="obs-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="bg-[#00677F] hover:bg-[#135163] text-white rounded-xl px-6 shadow-[0_0_15px_rgba(0,103,127,0.3)]"
                data-testid="obs-submit-btn"
              >
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
